import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { PortfolioAs } from '../PortfolioSubnav.component';
import { useLocation, useNavigate } from 'react-router-dom';
import MailIcon from 'icons/Mail.icon';
import useQueryParams from 'hooks/useQueryParams';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import NotesIcon from 'icons/Notes.icon';
import ChecklistIcon from 'icons/Checklist.icon';
import PlusIcon from 'icons/Plus.icon';
import ArrowReplaceIcon from 'icons/ArrowReplace.icon';
import GridIcon from 'icons/Grid.icon';
import LinkIcon from 'icons/Link.icon';
import BurgerIcon from 'icons/Burger.icon';
import SpeakerphoneIcon from 'icons/Speakerphone.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import SendIcon from 'icons/Send.icon';

const LEADS_PAGE = 'leads';
const LISTING_PAGE = 'listing-information';
const PAGES_WITH_DROPDOWN = ['deal', LISTING_PAGE, LEADS_PAGE];

type UseDropdownOptionsType = {
  as: PortfolioAs;
  isPrivate: boolean;
  isPublic: boolean;
  hasUnlockPremiumFeatures: boolean;
  isDisabled?: boolean;
  isDraft?: boolean;
  hasListing?: boolean;
  updateListingLink?: string;
  canSendBroadcastPulseCheck: boolean;
  canSendBroadcastMessage: boolean;
  onInvitePrivateListing: () => void;
  onMakeNewLead: () => void;
  onDealNote: () => void;
  openDealStatusDrawer?: () => void;
  copyPrivateLink: () => void;
  openBroadcastModal: () => void;
  onShareListing: () => void;
};

export default function useDropdownOptions(config: UseDropdownOptionsType) {
  const {
    as,
    isPrivate,
    isPublic,
    hasUnlockPremiumFeatures,
    isDisabled,
    isDraft,
    hasListing,
    updateListingLink,
    canSendBroadcastPulseCheck,
    canSendBroadcastMessage,
    onInvitePrivateListing,
    onMakeNewLead,
    onDealNote,
    openDealStatusDrawer,
    copyPrivateLink,
    openBroadcastModal,
    onShareListing,
  } = config;

  const isAboveLaptop = useMatchMedia('isAboveLaptop');

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const {
    params: { newNote },
  } = useQueryParams();

  const {
    isSuperAdmin,
    currentUser: { isGridViewForLeads },
    updateView,
  } = useContext(CurrentUserContext);

  const isOnPageWithDropdown = useMemo(
    () => PAGES_WITH_DROPDOWN.some((el) => pathname.includes(el)),
    [pathname],
  );

  const isOnListingInformationPage = useMemo(
    () => pathname.includes(LISTING_PAGE),
    [pathname],
  );

  const isOnLeadsPage = useMemo(
    () => pathname.includes(LEADS_PAGE),
    [pathname],
  );

  const showButtons = useMemo(
    () => (isSuperAdmin || hasUnlockPremiumFeatures) && isOnPageWithDropdown,
    [hasUnlockPremiumFeatures, isOnPageWithDropdown, isSuperAdmin],
  );

  const updateLeadsView = useCallback(
    () => updateView('isGridViewForLeads', !isGridViewForLeads),
    [isGridViewForLeads, updateView],
  );

  const sellerDropdown = useMemo(
    () =>
      isOnPageWithDropdown
        ? [
            {
              label: 'updateDealStatus',
              disabled: isDisabled,
              icon: <ArrowReplaceIcon strokeWidth="1.2" />,
              onClick: openDealStatusDrawer,
            },
            ...(showButtons
              ? [
                  {
                    label: 'addLead',
                    icon: PlusIcon,
                    disabled: isDisabled,
                    onClick: () => {
                      if (isDisabled) return;
                      onMakeNewLead();
                    },
                  },
                ]
              : []),
            ...(isPrivate && showButtons
              ? [
                  {
                    label: 'invitePrivateViewer',
                    icon: <MailIcon strokeWidth="1.6" />,
                    disabled: isDisabled,
                    onClick: () => {
                      if (isDisabled) return;
                      onInvitePrivateListing();
                    },
                  },
                  {
                    icon: LinkIcon,
                    label: 'copyPrivateLink',
                    disabled: isDisabled,
                    onClick: copyPrivateLink,
                  },
                ]
              : []),
            ...(isPublic && showButtons
              ? [
                  {
                    icon: SendIcon,
                    label: 'shareListing',
                    onClick: onShareListing,
                  },
                ]
              : []),
            {
              label: 'dealNotes',
              icon: <NotesIcon strokeWidth="0.8" />,
              onClick: onDealNote,
            },
            ...(!isOnListingInformationPage && isDraft
              ? [
                  {
                    label: 'finishListing',
                    disabled: isDisabled,
                    icon: <ChecklistIcon strokeWidth="1.4" />,
                    onClick: () => navigate(updateListingLink),
                  },
                ]
              : []),
            ...(!isOnListingInformationPage && hasListing
              ? [
                  {
                    label: 'manageListing',
                    disabled: isDisabled,
                    icon: <ChecklistIcon strokeWidth="1.4" />,
                    onClick: () => navigate(`./${LISTING_PAGE}`),
                  },
                ]
              : []),
            ...(isAboveLaptop && isOnLeadsPage && showButtons
              ? [
                  {
                    label: isGridViewForLeads
                      ? 'switchToTableView'
                      : 'switchToCardView',
                    icon: isGridViewForLeads ? (
                      <BurgerIcon strokeWidth="1.2" />
                    ) : (
                      <GridIcon strokeWidth="1.4" />
                    ),
                    onClick: updateLeadsView,
                  },
                ]
              : []),
            ...(canSendBroadcastMessage || canSendBroadcastPulseCheck
              ? [
                  {
                    label: canSendBroadcastMessage
                      ? 'broadcastMessage'
                      : 'broadcastPulseCheck',
                    icon: SpeakerphoneIcon,
                    onClick: openBroadcastModal,
                  },
                ]
              : []),
            {
              label: 'buyerQualifications',
              disabled: isDisabled,
              icon: <ChecklistIcon strokeWidth="1.4" />,
              onClick: () => navigate(`./qualification-questions`),
            },
          ]
        : [],
    [
      isOnPageWithDropdown,
      isOnListingInformationPage,
      isGridViewForLeads,
      isOnLeadsPage,
      isPrivate,
      isPublic,
      showButtons,
      isDisabled,
      isDraft,
      hasListing,
      updateListingLink,
      canSendBroadcastPulseCheck,
      canSendBroadcastMessage,
      isAboveLaptop,
      openDealStatusDrawer,
      onDealNote,
      copyPrivateLink,
      navigate,
      onInvitePrivateListing,
      onMakeNewLead,
      updateLeadsView,
      openBroadcastModal,
      onShareListing,
    ],
  );

  const buyerDropdown = useMemo(() => [], []);

  useEffect(() => {
    if (as === 'seller' && newNote) {
      onDealNote();
    }
  }, [as, newNote, onDealNote]);

  return {
    showButtons,
    dropdownOptions: { seller: sellerDropdown, buyer: buyerDropdown }[as],
  };
}
